import MenuIcon from '@mui/icons-material/Menu';
import {
  alpha,
  Box,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@parkdna/ui/theme';
import { memo } from 'react';
import { Menu, menuClasses, Sidebar as ProSidebar } from 'react-pro-sidebar';
import { MenuItemStyles } from 'react-pro-sidebar/dist/components/Menu';

import { useSidebarStore } from '@/stores/sidebar';

import { FAQLink } from './FAQLink';
import { SidebarHeader } from './SidebarHeader';
import { MenuItem, SidebarMenuItems } from './SidebarMenuItems';
import { SupportLink } from './SupportLink';

export type SidebarProps = {
  broken: boolean;
  setBroken: (broken: boolean) => void;
  menuItems: MenuItem[];
};

const Sidebar = ({ broken, setBroken, menuItems }: SidebarProps) => {
  const [theme] = useTheme();
  const [isSidebarCollapsed, isSidebarOpen, setIsSidebarOpen] = useSidebarStore(
    (state) => [state.isCollapsed, state.isOpen, state.setIsOpen]
  );
  const isOnSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const menuItemStyles: MenuItemStyles = {
    root: {
      padding: isSidebarCollapsed ? '0 0.05rem' : '0 0.5rem',
      fontSize: theme.typography.fontSize,
    },
    icon: {
      color: theme.palette.primary.main,
      [`&.${menuClasses.disabled}`]: {
        color: theme.palette.action.disabled,
      },
      width: 18,
      height: 20,
      minWidth: 18,
    },
    SubMenuExpandIcon: {
      color: theme.palette.primary.main,
    },
    subMenuContent: ({ level }) => ({
      color: theme.palette.text.primary,
      backgroundColor:
        level === 0 ? theme.palette.background.darker : 'transparent',
    }),
    button: {
      paddingLeft: isSidebarCollapsed ? 25 : 20,
      transition: `${theme.transitions.create(['background-color', 'color'], {
        duration: theme.transitions.duration.shorter,
      })}`,
      height: 40,
      borderRadius: `${theme.shape.borderRadius}px`,
      [`&.${menuClasses.disabled}`]: {
        color: theme.palette.action.disabled,
      },
      '&:hover': {
        backgroundColor: alpha(theme.palette.primary.main, 0.1),
      },
      '&.active': {
        backgroundColor: alpha(theme.palette.primary.main, 0.2),
        '& .MuiSvgIcon-root': {
          color: theme.palette.text.primary,
        },
      },
    },
    label: ({ open }) => ({
      fontWeight: open ? 600 : undefined,
    }),
  };

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        flexShrink: 0,
        transition: 'width,left,right,300ms',
        [theme.breakpoints.down('sm')]: {
          '& .ps-sidebar-container': {
            overflow: 'auto',
            scrollbarWidth: 'none',
            '-ms-overflow-style': 'none',
            '&::-webkit-scrollbar': {
              display: 'none',
            },
          },
        },
      }}
    >
      {broken && (
        <IconButton
          aria-label={
            isSidebarOpen ? 'Close Navigation Menu' : 'Open Navigation Menu'
          }
          aria-expanded={isSidebarOpen}
          sx={{
            position: 'absolute',
            top: 10,
            left: 10,
          }}
          onClick={() => {
            setIsSidebarOpen(!isSidebarOpen);
          }}
        >
          <MenuIcon />
        </IconButton>
      )}
      <ProSidebar
        ref={(_element) => {
          if (!isOnSmallScreen) {
            return;
          }

          // TODO: rethink implementation
          // if (!element) {
          //   clearAllBodyScrollLocks();
          //   return;
          // }
          //
          // disableBodyScroll(element);
        }}
        collapsed={isSidebarCollapsed}
        toggled={isSidebarOpen}
        breakPoint="md"
        onBreakPoint={setBroken}
        onBackdropClick={() => setIsSidebarOpen(!isSidebarOpen)}
        style={{
          height: '100%',
          position: 'fixed',
          zIndex: theme.zIndex.appBar,
        }}
        width={`${theme.layout.sidebar.regular}px`}
        backgroundColor={theme.palette.background.darker}
        collapsedWidth={`${theme.layout.sidebar.collapsed}px`}
        rootStyles={{
          borderRight: '1.25px solid',
          borderColor: theme.palette.divider,
        }}
      >
        <Box display="flex" flexDirection="column" minHeight="100vh">
          <SidebarHeader
            style={{
              marginBottom: isSidebarCollapsed ? '12px' : 0,
              marginTop: '8px',
            }}
          />
          <Box flex={1} marginBottom={4} marginTop={2}>
            <Menu menuItemStyles={menuItemStyles}>
              <SidebarMenuItems items={menuItems} />
            </Menu>
          </Box>
          <SupportLink />
          {import.meta.env.VITE_FAQ_URL && (
            <FAQLink faqUrl={import.meta.env.VITE_FAQ_URL} />
          )}
          {import.meta.env.VITE_VERSION && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                display: 'block',
                textAlign: 'center',
                py: 1,
                opacity: 0.7,
                fontSize: '0.7rem',
              }}
            >
              {isSidebarCollapsed ? 'v' : 'Version '}
              {import.meta.env.VITE_VERSION}
            </Typography>
          )}
        </Box>
      </ProSidebar>
    </Box>
  );
};

export default memo(Sidebar);
